import React, { useEffect, useRef } from 'react';

/**
 * AuroraFlower — phyllotaxis "Fibonacci flower" background.
 *
 * 1597 nodes (a Fibonacci number) placed on the golden angle (137.5°), colored
 * along the Studio logo gradient (blue core → violet → deep-violet rim), with
 * spiral-arm threads and a few violet packets orbiting the arms.
 *
 * Pure canvas: only the 2D context animates (no live CSS blur), so it stays
 * GPU-cheap. Respects prefers-reduced-motion — renders a single static frame.
 *
 * Sizes itself to its own wrapper (via ResizeObserver), so it works both as a
 * full-viewport background (position="fixed") and inside a bounded box
 * (position="absolute" within a relative parent).
 *
 * Props:
 *   focusX        — horizontal center of the bloom, 0–1 (default 0.5)
 *   position      — 'fixed' (full viewport) | 'absolute' (fill parent) (default 'fixed')
 *   vignette      — render the seating vignette overlay (default true)
 *   vignetteColor — outer color the vignette fades to (default '#060509')
 *   cover         — size the bloom to the box diagonal so it fills the whole
 *                   rectangle (corners included), even at extreme aspect ratios
 *                   like a short/wide hero strip. Default false (min-dimension
 *                   bloom, used by the full-viewport landing/auth backgrounds).
 *   className     — extra classes on the wrapper
 */
const AuroraFlower = ({
  focusX = 0.5,
  position = 'fixed',
  vignette = true,
  vignetteColor = '#060509',
  cover = false,
  className = '',
}) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const cv = canvasRef.current;
    if (!wrap || !cv) return;
    const ctx = cv.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let W, H, DPR, cx, cy, scale, raf;
    const N = 1597;                          // a Fibonacci number
    const GA = Math.PI * (3 - Math.sqrt(5)); // golden angle ≈ 137.5°
    const nodes = [];

    const build = () => {
      nodes.length = 0;
      for (let i = 0; i < N; i++) {
        const r = scale * Math.sqrt(i), t = i * GA;
        nodes.push({
          x: cx + r * Math.cos(t),
          y: cy + r * Math.sin(t),
          i,
          s: (0.7 + (i / N) * 1.5) * DPR,
          ph: Math.random() * Math.PI * 2,
        });
      }
    };

    const resize = () => {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      const w = wrap.clientWidth || window.innerWidth;
      const h = wrap.clientHeight || window.innerHeight;
      W = cv.width = w * DPR;
      H = cv.height = h * DPR;
      cv.style.width = w + 'px';
      cv.style.height = h + 'px';
      cx = W * focusX;
      cy = H * 0.5;
      // `cover` scales to the diagonal so the disk reaches every corner of a
      // wide/short box; otherwise scale to the min dimension (centered bloom).
      scale = cover ? Math.hypot(W, H) * 0.0132 : Math.min(W, H) * 0.0235;
      build();
    };

    const packets = Array.from({ length: 5 }, (_, k) => ({
      pos: k * (N / 5),
      spd: 1.3 + Math.random() * 1.1,
    }));

    const t0 = performance.now();
    const frame = (now) => {
      const el = (now - t0) / 1000;
      ctx.clearRect(0, 0, W, H);

      // Fibonacci spiral-arm threads (connect i to i+13)
      ctx.lineWidth = 0.6 * DPR;
      for (let i = 0; i < N - 13; i++) {
        const grow = reduce ? 1 : Math.min(1, Math.max(0, (el * 260 - i) / 120));
        if (grow <= 0) continue;
        const a = nodes[i], b = nodes[i + 13];
        ctx.strokeStyle = 'rgba(124,58,237,' + (0.06 * grow) + ')';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      // nodes — appear sequentially (growth), then gentle breathing
      for (let i = 0; i < N; i++) {
        const n = nodes[i];
        const appear = reduce ? 1 : Math.min(1, Math.max(0, (el * 300 - i) / 40));
        if (appear <= 0) continue;
        const breathe = reduce ? 1 : 0.6 + 0.4 * Math.sin(el * 1.3 + n.ph + n.i * 0.02);
        const a = appear * breathe, mix = n.i / N;
        const cr = Math.round(96 + 28 * mix);
        const cg = Math.round(165 - 107 * mix);
        const cb = Math.round(250 - 13 * mix);
        ctx.beginPath();
        ctx.fillStyle = 'rgba(' + cr + ',' + cg + ',' + cb + ',' + (0.2 + a * 0.55) + ')';
        ctx.arc(n.x, n.y, n.s * (0.7 + appear * 0.5), 0, Math.PI * 2);
        ctx.fill();
      }

      // orbiting packets (glow)
      if (!reduce) {
        for (const p of packets) {
          p.pos += p.spd;
          if (p.pos >= N) p.pos -= N;
          const n = nodes[Math.floor(p.pos)];
          if (!n) continue;
          const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 9 * DPR);
          g.addColorStop(0, 'rgba(220,210,255,0.9)');
          g.addColorStop(0.4, 'rgba(167,139,250,0.5)');
          g.addColorStop(1, 'rgba(167,139,250,0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 9 * DPR, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (!reduce) raf = requestAnimationFrame(frame);
    };

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(resize);
      ro.observe(wrap);
    } else {
      window.addEventListener('resize', resize, { passive: true });
    }
    resize();
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', resize);
    };
  }, [focusX, cover]);

  return (
    <div
      ref={wrapRef}
      className={`${position === 'fixed' ? 'fixed' : 'absolute'} inset-0 z-0 pointer-events-none ${className}`}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      {vignette && (
        <div
          className="absolute inset-0"
          style={{
            background:
              `radial-gradient(ellipse 85% 90% at ${focusX * 100}% 46%, transparent 38%, rgba(6,5,9,0.5) 78%, ${vignetteColor} 100%)`,
          }}
        />
      )}
    </div>
  );
};

export default AuroraFlower;
